import findKey from 'lodash/findKey';

import { getCookie } from '../get-cookie';
import type { GroupCode, GroupName } from './one-trust-categorised-cookies';
import {
  cookiesPrefixNameWithGroupMap,
  groups,
} from './one-trust-categorised-cookies';
import type { setCookieIfConsentIsGiven } from './set-cookie-if-consent-is-given';

export const oneTrustConsentCookieName = 'OptanonConsent';

export const isValidGroupCode = (
  groupCode: GroupCode | string
): groupCode is GroupCode => Object.keys(groups).includes(groupCode);

type OtConsentType = '0' | '1';
export const isValidConsentType = (
  consentType: OtConsentType | string
): consentType is OtConsentType => ['0', '1'].includes(consentType);

type CookieOptions = Parameters<typeof setCookieIfConsentIsGiven>[2];

export const getOtGroups = (cookieOptions?: CookieOptions) => {
  const otCookieValue = getCookie(oneTrustConsentCookieName, cookieOptions);
  if (!otCookieValue) {
    return null;
  }
  const params = new URLSearchParams(otCookieValue);
  const otGroups = params.get('groups')?.split(',');
  if (otGroups) {
    return otGroups;
  }
  return null;
};

export const getGroupByGroupName = (groupNameToFind: GroupName) => {
  const groupCode = findKey(groups, ['name', groupNameToFind]) as GroupCode;
  return { ...groups[groupCode], groupCode };
};

const timeIntervalInMilliSecondsToCheckOtCookie = 10;
const timeoutInMilliSecondsToCheckOtCookie = 3000;

const resolveWhenIdle = (callback: () => void): void => {
  if (typeof requestIdleCallback !== 'undefined') {
    requestIdleCallback(callback, { timeout: 1000 });
  } else {
    // Fallback for older browsers
    setTimeout(callback, 0);
  }
};

export const getGroupsFromOtCookieWhenAvailable = (
  cookieOptions?: CookieOptions
): Promise<string[] | null> =>
  new Promise((resolve) => {
    const otGroups = getOtGroups(cookieOptions);
    if (otGroups) {
      // Defer tag firing to idle time to avoid burst during interaction window
      resolveWhenIdle(() => resolve(otGroups));
      return;
    }

    const timer = setInterval(() => {
      const otGroups = getOtGroups(cookieOptions);
      if (otGroups) {
        clearInterval(timer);
        // Defer tag firing to idle time to avoid burst during interaction window
        resolveWhenIdle(() => resolve(otGroups));
      }
    }, timeIntervalInMilliSecondsToCheckOtCookie);

    setTimeout(() => {
      clearInterval(timer);
      return resolve(null);
    }, timeoutInMilliSecondsToCheckOtCookie);
  });

export const getGroupForWhichCookieWillBeCreated = (
  cookieNameToBeCreated: string
) => {
  const foundCookiesPrefixNameWithGroupMap = Object.entries(
    cookiesPrefixNameWithGroupMap
  ).find(([mappedCookieName]) =>
    cookieNameToBeCreated.startsWith(mappedCookieName)
  );

  if (!foundCookiesPrefixNameWithGroupMap) {
    return null;
  }

  const groupCode = foundCookiesPrefixNameWithGroupMap[1];
  const group = groups[groupCode];

  return { groupCode, ...group };
};
